import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Drawer, Button, ScrollArea } from '@mantine/core';
import { useApiClient } from '../../api/ApiClientContext';
import { HabitImpactGroup } from '../../api/Api';
import { Icon } from '../../assets/icons/Icon';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import styles from './PositiveImpactsDrawer.module.scss';

dayjs.extend(relativeTime);

interface PositiveImpactsDrawerProps {
  opened: boolean;
  onClose: () => void;
}

const HABIT_ICON_COLORS: Record<string, string> = {
  'user-check': '#EDE9FE',
  'checklist': '#DBEAFE',
  'discount-check': '#D1FAE5',
};
const DEFAULT_ICON_BG = '#EDE9FE';

const formatImpactDate = (dateStr: string): string => {
  const date = dayjs(dateStr);
  const now = dayjs();
  const diffHours = now.diff(date, 'hour');
  if (diffHours < 24) return date.fromNow();
  return date.format('DD MMM, YYYY');
};

const HabitGroup: React.FC<{ habit: HabitImpactGroup }> = ({ habit }) => {
  const iconBg = HABIT_ICON_COLORS[habit.habit_icon ?? ''] ?? DEFAULT_ICON_BG;

  return (
    <div className={styles.habitGroup}>
      <div className={styles.habitRow}>
        <div className={styles.habitIconWrap} style={{ backgroundColor: iconBg }}>
          <Icon iconName="PositiveImpactIcon" width="1.25rem" height="1.25rem" />
        </div>
        <div className={styles.habitInfo}>
          <span className={styles.habitName}>{habit.habit_name}</span>
          <span className={styles.habitCount}>
            {habit.impact_count} impact{habit.impact_count !== 1 ? 's' : ''} logged
          </span>
        </div>
      </div>

      <div className={styles.impactsList}>
        {habit.impacts.map((impact, idx) => (
          <div
            key={idx}
            className={`${styles.impactRow} ${idx === habit.impacts.length - 1 ? styles.impactRowLast : ''}`}
          >
            <div className={styles.dotWrap}>
              <Icon iconName="TimeLineBlueIcon" width="1.25rem" height="1.25rem" />
            </div>
            <div className={styles.impactContent}>
              <span className={styles.impactDescription}>{impact.description}</span>
              <span className={styles.impactDate}>{formatImpactDate(impact.logged_at)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const PositiveImpactsDrawer: React.FC<PositiveImpactsDrawerProps> = ({ opened, onClose }) => {
  const apiClient = useApiClient();

  const { data, isLoading } = useQuery({
    queryKey: ['insightsPositiveImpacts'],
    queryFn: async () => {
      const res = await apiClient.api.getPositiveImpactsApiV1InsightsPositiveImpactsGet({ limit: 100 });
      return res?.data;
    },
    enabled: opened,
  });

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title="Positive impacts"
      position="right"
      size="23rem"
      radius={0}
      scrollAreaComponent={ScrollArea.Autosize}
      classNames={{
        title: styles.drawerTitle,
        body: styles.drawerBody,
        header: styles.drawerHeader,
      }}
    >
      <div className={styles.content}>
        {isLoading ? (
          <div className={styles.emptyState}>Loading...</div>
        ) : !data?.habits?.length ? (
          <div className={styles.emptyState}>No positive impacts yet</div>
        ) : (
          <>
            <span className={styles.countLabel}>
              {data.total_count} POSITIVE IMPACT{data.total_count !== 1 ? 'S' : ''}
            </span>
            <div className={styles.habitGroups}>
              {data.habits.map((habit) => (
                <HabitGroup key={habit.habit_id} habit={habit} />
              ))}
            </div>
          </>
        )}
      </div>

      <div className={styles.footer}>
        <Button variant="default" radius={16} onClick={onClose} size="sm">
          Close
        </Button>
      </div>
    </Drawer>
  );
};

export default PositiveImpactsDrawer;
